import React, { useState } from 'react'
import { Card, CardContent } from '../../ui/card'
import { Button } from '../../ui/button'
import { Label } from '../../ui/label'
import { DatePicker } from '../../ui/date-picker'
import { EventCombobox } from '../../ui/event-combobox'
import { Loader2, FileSpreadsheet } from 'lucide-react'
import { toast } from 'sonner'
import { extractErrorMessage } from '@/common/extractErrorMessage'
import { useApplicationStore } from '../../../stores/useApplicationStore'
import ReportEndpoints from '../../../endpoints/ReportEndpoints'
import { DownloadBlob } from '../../../common/Util'
import { ReportViewer, useReportViewer } from '../ReportViewer'
import { RoleSelect } from '../RoleSelect'
import { useReportEvent } from '../useReportEvent'
import { rootUrl } from '../reportShared'

export const PunchClockErrorsPage: React.FC = () => {
  const { events } = useApplicationStore()
  const { viewerProps, openReport, generating, setGenerating } = useReportViewer()

  const [roles, setRoles] = useState<number[]>([])
  const [date, setDate] = useState<Date | undefined>()

  const { selectedEventId, eventRoles, loadingRoles, selectEvent } = useReportEvent((start) => {
    setDate(start)
  })

  const validate = (): boolean => {
    if (!selectedEventId || roles.length === 0 || !date) {
      toast.error('Fill in all required fields')
      return false
    }
    return true
  }

  const generate = async () => {
    if (!validate()) return
    setGenerating(true)
    try {
      const res = await ReportEndpoints.getPunchClockErrorNoteReport({
        eventId: parseInt(selectedEventId), roles, date: date!.getTime() as any, rootUrl,
      })
      openReport('Punch Clock Entries with Notes/Errors', res)
    } catch (error) { toast.error(extractErrorMessage(error, 'Failed to generate report')) }
    finally { setGenerating(false) }
  }

  const exportExcel = async () => {
    if (!validate()) return
    setGenerating(true)
    try {
      const res = await ReportEndpoints.getPunchClockErrorNoteExcel({
        eventId: parseInt(selectedEventId), roles, date: date!.getTime() as any, rootUrl,
      })
      DownloadBlob(await res.blob(), 'punch-clock-notes-errors.xlsx')
    } catch (error) { toast.error(extractErrorMessage(error, 'Failed to export Excel')) }
    finally { setGenerating(false) }
  }

  return (
    <>
      <Card className="max-w-3xl">
        <CardContent className="pt-6 space-y-4">
          <div className="space-y-2">
            <Label>Event *</Label>
            <EventCombobox events={events} value={selectedEventId} onChange={selectEvent} />
          </div>
          {selectedEventId && (
            <>
              <div className="space-y-2">
                <Label>Roles * (schedulable only)</Label>
                {loadingRoles ? <Loader2 className="h-4 w-4 animate-spin" /> : <RoleSelect roles={eventRoles} selected={roles} onChange={setRoles} schedulableOnly />}
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <DatePicker date={date} onDateChange={setDate} placeholder="Select date" />
              </div>
            </>
          )}
          <div className="flex gap-2">
            <Button onClick={generate} disabled={generating || !selectedEventId}>
              {generating ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Generating...</> : 'Generate Report'}
            </Button>
            <Button variant="outline" onClick={exportExcel} disabled={generating || !selectedEventId}>
              <FileSpreadsheet className="mr-2 h-4 w-4" />Export to Excel
            </Button>
          </div>
        </CardContent>
      </Card>
      <ReportViewer {...viewerProps} />
    </>
  )
}
